import React, { useState, useEffect } from 'react';
import { Plus, ClipboardList, X, Trash2, Loader2, Package, Building2, Calendar } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function POSAdminPurchaseOrders() {
  const [pos, setPos] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [supplierMode, setSupplierMode] = useState('existing'); // existing | new
  const [form, setForm] = useState({
    supplier_id: '',
    supplier_name: '',
    supplier_phone: '',
    expected_date: '',
    notes: '',
    items: [],
  });
  const [newItem, setNewItem] = useState({ product_id: '', product_name: '', quantity: 1, unit_cost: 0 });
  const token = localStorage.getItem('pos_admin_token');

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [pr, sr, prr] = await Promise.all([
        fetch(`${API_URL}/api/admin/pos/purchase-orders`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/api/admin/pos/suppliers`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/api/admin/pos/products?filter=all`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      const [poData, supData, prodData] = await Promise.all([pr.json(), sr.json(), prr.json()]);
      setPos(poData || []);
      setSuppliers(supData || []);
      setProducts(prodData?.products || []);
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const addItem = () => {
    if (!newItem.product_id || newItem.quantity < 1) {
      toast.error('Select product and quantity');
      return;
    }
    const prod = products.find((p) => p._id === newItem.product_id);
    setForm({
      ...form,
      items: [
        ...form.items,
        {
          product_id: newItem.product_id,
          product_name: prod?.name || newItem.product_name,
          quantity: parseInt(newItem.quantity),
          unit_cost: parseFloat(newItem.unit_cost) || 0,
        },
      ],
    });
    setNewItem({ product_id: '', product_name: '', quantity: 1, unit_cost: 0 });
  };

  const removeItem = (idx) => {
    setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });
  };

  const submitPO = async () => {
    if (supplierMode === 'existing' && !form.supplier_id) {
      toast.error('Choose a supplier');
      return;
    }
    if (supplierMode === 'new' && !form.supplier_name) {
      toast.error('Enter supplier name');
      return;
    }
    if (form.items.length === 0) {
      toast.error('Add at least one item');
      return;
    }
    try {
      const payload = { ...form, supplier_mode: supplierMode };
      const res = await fetch(`${API_URL}/api/admin/pos/purchase-orders`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed');
      }
      toast.success('Purchase Order created');
      setShowModal(false);
      setForm({ supplier_id: '', supplier_name: '', supplier_phone: '', expected_date: '', notes: '', items: [] });
      fetchAll();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const total = form.items.reduce((s, i) => s + i.quantity * i.unit_cost, 0);

  return (
    <div className="space-y-6" data-testid="pos-admin-pos">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Purchase Orders</h1>
          <p className="text-gray-500">Create POs for suppliers; store managers receive stock (GRN)</p>
        </div>
        <Button
          onClick={() => setShowModal(true)}
          className="bg-slate-900 hover:bg-slate-800 text-white"
          data-testid="create-po-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Purchase Order
        </Button>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
        </div>
      ) : pos.length === 0 ? (
        <div className="bg-white rounded-xl p-12 border text-center text-gray-500">
          <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No purchase orders yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {pos.map((po) => (
            <div key={po._id} className="bg-white rounded-xl p-5 border shadow-sm">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-gray-900">{po.po_number}</h3>
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        po.status === 'received'
                          ? 'bg-emerald-100 text-emerald-700'
                          : po.status === 'partial'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}
                    >
                      {po.status?.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <Building2 className="w-4 h-4" /> {po.supplier_name}
                    </div>
                    <div className="flex items-center gap-1">
                      <Package className="w-4 h-4" /> {po.items?.length || 0} items
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" /> Created{' '}
                      {new Date(po.created_at).toLocaleDateString('en-IN')}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold">₹{po.total_amount?.toFixed(2)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">New Purchase Order</h2>
              <button onClick={() => setShowModal(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Supplier choice */}
            <div className="mb-4">
              <div className="flex gap-2 mb-3">
                <button
                  onClick={() => setSupplierMode('existing')}
                  className={`px-3 py-1.5 rounded-lg text-sm ${
                    supplierMode === 'existing' ? 'bg-slate-900 text-white' : 'bg-gray-100'
                  }`}
                >
                  Existing Supplier
                </button>
                <button
                  onClick={() => setSupplierMode('new')}
                  className={`px-3 py-1.5 rounded-lg text-sm ${
                    supplierMode === 'new' ? 'bg-slate-900 text-white' : 'bg-gray-100'
                  }`}
                >
                  New Supplier
                </button>
              </div>

              {supplierMode === 'existing' ? (
                <select
                  value={form.supplier_id}
                  onChange={(e) => setForm({ ...form, supplier_id: e.target.value })}
                  className="w-full h-10 px-3 rounded-lg border bg-white"
                  data-testid="po-supplier-select"
                >
                  <option value="">Select supplier...</option>
                  {suppliers.map((s) => (
                    <option key={s._id} value={s._id}>
                      {s.name} {s.phone ? `(${s.phone})` : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    placeholder="Supplier name"
                    value={form.supplier_name}
                    onChange={(e) => setForm({ ...form, supplier_name: e.target.value })}
                  />
                  <Input
                    placeholder="Phone"
                    value={form.supplier_phone}
                    onChange={(e) => setForm({ ...form, supplier_phone: e.target.value })}
                  />
                </div>
              )}
            </div>

            {/* Expected date */}
            <div className="mb-4">
              <label className="text-sm font-medium">Expected Delivery Date</label>
              <Input
                type="date"
                value={form.expected_date}
                onChange={(e) => setForm({ ...form, expected_date: e.target.value })}
              />
            </div>

            {/* Items */}
            <div className="mb-4">
              <label className="text-sm font-medium mb-2 block">Items</label>
              <div className="grid grid-cols-12 gap-2 mb-2">
                <select
                  value={newItem.product_id}
                  onChange={(e) => setNewItem({ ...newItem, product_id: e.target.value })}
                  className="col-span-6 h-10 px-3 rounded-lg border bg-white"
                >
                  <option value="">Select product...</option>
                  {products.map((p) => (
                    <option key={p._id} value={p._id}>
                      {p.name} ({p.sku})
                    </option>
                  ))}
                </select>
                <Input
                  type="number"
                  placeholder="Qty"
                  value={newItem.quantity}
                  onChange={(e) => setNewItem({ ...newItem, quantity: e.target.value })}
                  className="col-span-2"
                />
                <Input
                  type="number"
                  placeholder="Unit ₹"
                  value={newItem.unit_cost}
                  onChange={(e) => setNewItem({ ...newItem, unit_cost: e.target.value })}
                  className="col-span-3"
                />
                <Button onClick={addItem} className="col-span-1" data-testid="add-po-item">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              {form.items.length > 0 && (
                <div className="border rounded-lg divide-y mt-3">
                  {form.items.map((it, idx) => (
                    <div key={idx} className="flex items-center justify-between px-3 py-2 text-sm">
                      <div>{it.product_name}</div>
                      <div className="flex items-center gap-4">
                        <span>
                          {it.quantity} × ₹{it.unit_cost} = ₹{(it.quantity * it.unit_cost).toFixed(2)}
                        </span>
                        <button onClick={() => removeItem(idx)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </div>
                  ))}
                  <div className="flex items-center justify-between px-3 py-2 bg-gray-50 text-sm font-bold">
                    <span>Total</span>
                    <span>₹{total.toFixed(2)}</span>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="text-sm font-medium">Notes</label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full h-20 px-3 py-2 rounded-lg border"
              />
            </div>

            <div className="flex gap-3 mt-5">
              <Button onClick={submitPO} className="flex-1 bg-slate-900 text-white" data-testid="submit-po-btn">
                Create PO
              </Button>
              <Button variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
